import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiFileText } from "react-icons/fi";
import {
  NegotiationRoundStatus,
  useListNegotiationRoundsV2Query,
  type NegotiationRoundV2Dto,
  type SourcingEventV2Dto,
  type ViewDocumentAsOwnerDto,
} from "../../../../autogen/bff-api";
import { CombinedDocuments } from "../../../../common/documents/CombinedDocuments";
import { DocumentsAsOwnerTable } from "../../../../common/documents/DocumentsAsOwnerTable";
import {
  IncompleteFileUpload,
  isIncompleteFileUpload,
  UploadCompletedStatus,
} from "../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../common/documents/UppyUploader/useUppyUploader";
import { SectionCard } from "../../../sourcing-events/portal/view-single/SectionCard";

/**
 * ADR-033 D2: the round's documents on the round-scoped request tab — uploadable like V1
 * until the round completes, read-only afterwards.
 */
export const RoundDocumentsSection = ({
  event,
  round,
}: {
  event: SourcingEventV2Dto;
  round: NegotiationRoundV2Dto;
}) => {
  const { t } = useTranslation();
  const { refetch } = useListNegotiationRoundsV2Query({ eventId: event.id });
  const readOnly = round.status === NegotiationRoundStatus.Completed;

  const { uppy, allFiles, documents, setDocuments, removeUpload, reloadEntity } = useUppyUploader({
    entityType: "NegotiationRoundV2",
    entityId: round.id,
    eventId: event.id,
    reloadEntity: refetch,
  });

  useEffect(() => {
    setDocuments(round.documents ?? []);
  }, [round.documents, setDocuments]);

  return (
    <SectionCard icon={FiFileText} title={t("Documents")} count={(round.documents ?? []).length}>
      {readOnly ? (
        <DocumentsAsOwnerTable documents={round.documents ?? []} />
      ) : (
        <CombinedDocuments
          uploadedDocuments={documents}
          setUploadedDocuments={setDocuments}
          removeUploadedDocument={(document: ViewDocumentAsOwnerDto) =>
            removeUpload({
              status: UploadCompletedStatus.UploadCompleted,
              documentId: document.id,
              uploadId: document.fromUploadId,
              ...document,
            })
          }
          canEditTextDocuments={false}
          canDeleteDocuments
          canEditDocumentNames
          isTagsColumnVisible={false}
          documentUploadConfig={{
            uppy,
            dropTargetId: `round-documents-${round.id}`,
            dropTargetHelperText: t("Upload documents for this negotiation round"),
            uploads: allFiles.filter(isIncompleteFileUpload) as IncompleteFileUpload[],
            removeUpload,
          }}
          refetchDocuments={() => {
            reloadEntity();
          }}
        />
      )}
    </SectionCard>
  );
};
